import React from 'react';
import Modal from './Modal';
import { useDialogs } from './Dialogs';
import { currentMessages, useLanguage, useMessages } from '../i18n/I18n';
import { LANGUAGES, LANGUAGE_NAMES, Language } from '../i18n/language';
import { updateCurrentUserLanguage } from '../services/api';
import { readSession, updateSessionUser } from '../services/sessionCookie';

interface SettingsDialogProps {
  sessionToken: string;
  festivalId: string;
  onClose: () => void;
}

/** The signed-in user's settings, saved on their account */
const SettingsDialog: React.FC<SettingsDialogProps> = ({ sessionToken, festivalId, onClose }) => {
  const t = useMessages('settings');
  const common = useMessages('common');
  const { language, setLanguage } = useLanguage();
  const { alert } = useDialogs();
  const email = readSession()?.user?.email ?? '';

  // Applies at once, then saves; a failed save goes back to the previous language, which a reload would show anyway
  const chooseLanguage = async (next: Language) => {
    if (next === language) return;
    const previous = language;
    setLanguage(next);
    try {
      await updateCurrentUserLanguage(sessionToken, festivalId, next);
      updateSessionUser({ language: next });
    } catch (error) {
      console.error('Failed to save the language:', error);
      setLanguage(previous);
      await alert(currentMessages('settings').languageNotSaved);
    }
  };

  return (
    <Modal isOpen onClose={onClose} className="settings-dialog">
      <h3>{t.title}</h3>
      <div className="settings-field">
        <span id="settings-language" className="settings-label">{t.language}</span>
        <div className="segmented" role="radiogroup" aria-labelledby="settings-language">
          {LANGUAGES.map(option => (
            <button
              key={option}
              type="button"
              role="radio"
              lang={option}
              aria-checked={option === language}
              className={option === language ? 'active' : undefined}
              onClick={() => chooseLanguage(option)}
            >
              {LANGUAGE_NAMES[option]}
            </button>
          ))}
        </div>
        <p className="settings-hint">{t.languageHint(email)}</p>
      </div>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>{common.close}</button>
      </div>
    </Modal>
  );
};

export default SettingsDialog;
